import { useState } from "react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Shield, Crown, User as UserIcon, Ban, CheckCircle, Trash2, ArrowUpDown, Coins, TrendingUp, TrendingDown } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

type UserRole = 'admin' | 'paid' | 'student' | 'free';
type SortField = 'email' | 'created_at' | 'role' | 'queryCount' | 'totalTokens' | 'totalCost' | 'availableTokens' | 'contributions' | 'grossProfit';
type SortOrder = 'asc' | 'desc';

interface UserCredits {
  credits_balance: number;
  credits_from_purchases: number;
  credits_from_membership: number;
}

interface UserWithRole {
  id: string;
  email: string;
  created_at: string;
  role: UserRole | null;
  isBanned: boolean;
  banReason: string | null;
  usage: {
    totalTokens: number;
    totalCost: number;
    queryCount: number;
  };
  credits?: UserCredits | null;
  totalContributions?: number;
}

interface UsersTableProps {
  users: UserWithRole[];
  currentUserId?: string;
  onRoleChange: (userId: string, newRole: UserRole) => void;
  onBan: (userId: string, email: string) => void;
  onUnban: (userId: string, email: string) => void;
  onDelete: (userId: string, email: string) => void;
  onRefresh?: () => void;
}

export function UsersTable({
  users,
  currentUserId,
  onRoleChange,
  onBan,
  onUnban,
  onDelete,
  onRefresh,
}: UsersTableProps) {
  const [searchQuery, setSearchQuery] = useState("");
  const [roleFilter, setRoleFilter] = useState<string>("all");
  const [statusFilter, setStatusFilter] = useState<string>("all");
  const [sortField, setSortField] = useState<SortField>('created_at');
  const [sortOrder, setSortOrder] = useState<SortOrder>('desc');
  
  // Token adjustment dialog state
  const [adjustDialog, setAdjustDialog] = useState<{ userId: string; email: string; currentBalance: number } | null>(null);
  const [adjustAmount, setAdjustAmount] = useState("");
  const [adjustType, setAdjustType] = useState<"add" | "subtract" | "set">("add");
  const [adjustReason, setAdjustReason] = useState("");
  const [isAdjusting, setIsAdjusting] = useState(false);

  const handleSort = (field: SortField) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('asc');
    }
  };

  const getRoleBadge = (role: UserRole | null) => {
    if (role === 'admin') {
      return <Badge variant="destructive" className="gap-1"><Shield className="h-3 w-3" /> Admin</Badge>;
    } else if (role === 'paid') {
      return <Badge variant="default" className="gap-1"><Crown className="h-3 w-3" /> Paid</Badge>;
    } else if (role === 'student') {
      return <Badge variant="secondary" className="gap-1"><UserIcon className="h-3 w-3" /> Student</Badge>;
    } else {
      return <Badge variant="outline" className="gap-1"><UserIcon className="h-3 w-3" /> Free</Badge>;
    }
  };

  const getGrossProfit = (user: UserWithRole) => {
    const contributions = user.totalContributions || 0;
    const costs = user.usage.totalCost || 0;
    return contributions - costs;
  };

  const getAvailableTokens = (user: UserWithRole) => {
    return user.credits?.credits_balance || 0;
  };

  const handleAdjustTokens = async () => {
    if (!adjustDialog || !adjustAmount || !adjustReason) {
      toast.error("Please fill in all fields");
      return;
    }

    const amount = parseInt(adjustAmount);
    if (isNaN(amount) || amount < 0) {
      toast.error("Please enter a valid positive number");
      return;
    }

    setIsAdjusting(true);
    try {
      const { data, error } = await supabase.functions.invoke('adjust-user-tokens', {
        body: {
          userId: adjustDialog.userId,
          amount,
          type: adjustType,
          reason: adjustReason,
        },
      });

      if (error) throw error;
      if (data?.error) throw new Error(data.error);

      toast.success(`Tokens adjusted: ${data.previousBalance} → ${data.newBalance}`);
      setAdjustDialog(null);
      setAdjustAmount("");
      setAdjustReason("");
      onRefresh?.();
    } catch (error) {
      console.error('Error adjusting tokens:', error);
      toast.error(error instanceof Error ? error.message : "Failed to adjust tokens");
    } finally {
      setIsAdjusting(false);
    }
  };

  const filteredAndSortedUsers = users
    .filter(user => {
      const matchesSearch = user.email.toLowerCase().includes(searchQuery.toLowerCase());
      const matchesRole = roleFilter === "all" || user.role === roleFilter;
      const matchesStatus = statusFilter === "all" || 
        (statusFilter === "banned" && user.isBanned) ||
        (statusFilter === "active" && !user.isBanned);
      return matchesSearch && matchesRole && matchesStatus;
    })
    .sort((a, b) => {
      let comparison = 0;
      
      switch (sortField) {
        case 'email':
          comparison = a.email.localeCompare(b.email);
          break;
        case 'created_at':
          comparison = new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
          break;
        case 'role':
          comparison = (a.role || 'free').localeCompare(b.role || 'free');
          break;
        case 'queryCount':
          comparison = a.usage.queryCount - b.usage.queryCount;
          break;
        case 'totalTokens':
          comparison = a.usage.totalTokens - b.usage.totalTokens;
          break;
        case 'totalCost':
          comparison = a.usage.totalCost - b.usage.totalCost;
          break;
        case 'availableTokens':
          comparison = getAvailableTokens(a) - getAvailableTokens(b);
          break;
        case 'contributions':
          comparison = (a.totalContributions || 0) - (b.totalContributions || 0);
          break;
        case 'grossProfit':
          comparison = getGrossProfit(a) - getGrossProfit(b);
          break;
      }
      
      return sortOrder === 'asc' ? comparison : -comparison;
    });

  return (
    <div className="space-y-4">
      {/* Filters */}
      <div className="flex flex-col sm:flex-row gap-4">
        <Input
          placeholder="Search by email..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          className="sm:max-w-xs"
        />
        <Select value={roleFilter} onValueChange={setRoleFilter}>
          <SelectTrigger className="sm:w-[180px]">
            <SelectValue placeholder="Filter by role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Roles</SelectItem>
            <SelectItem value="free">Free</SelectItem>
            <SelectItem value="student">Student & Educator</SelectItem>
            <SelectItem value="paid">Paid</SelectItem>
            <SelectItem value="admin">Admin</SelectItem>
          </SelectContent>
        </Select>
        <Select value={statusFilter} onValueChange={setStatusFilter}>
          <SelectTrigger className="sm:w-[180px]">
            <SelectValue placeholder="Filter by status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="banned">Banned</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Results count */}
      <div className="text-sm text-muted-foreground">
        Showing {filteredAndSortedUsers.length} of {users.length} users
      </div>

      {/* Table */}
      <div className="rounded-md border overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>
                <Button variant="ghost" size="sm" className="h-8 font-medium" onClick={() => handleSort('email')}>
                  Email <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead>
                <Button variant="ghost" size="sm" className="h-8 font-medium" onClick={() => handleSort('role')}>
                  Role <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead>Status</TableHead>
              <TableHead>
                <Button variant="ghost" size="sm" className="h-8 font-medium" onClick={() => handleSort('created_at')}>
                  Joined <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead className="text-right">
                <Button variant="ghost" size="sm" className="h-8 font-medium" onClick={() => handleSort('availableTokens')}>
                  Available <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead className="text-right">
                <Button variant="ghost" size="sm" className="h-8 font-medium" onClick={() => handleSort('totalCost')}>
                  Usage Cost <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead className="text-right">
                <Button variant="ghost" size="sm" className="h-8 font-medium" onClick={() => handleSort('contributions')}>
                  Contributed <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead className="text-right">
                <Button variant="ghost" size="sm" className="h-8 font-medium" onClick={() => handleSort('grossProfit')}>
                  Profit <ArrowUpDown className="ml-2 h-4 w-4" />
                </Button>
              </TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {filteredAndSortedUsers.length === 0 ? (
              <TableRow>
                <TableCell colSpan={9} className="text-center text-muted-foreground">
                  No users found
                </TableCell>
              </TableRow>
            ) : (
              filteredAndSortedUsers.map((user) => {
                const grossProfit = getGrossProfit(user);
                const availableTokens = getAvailableTokens(user);
                
                return (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">
                      <div className="space-y-1">
                        <div className="max-w-[200px] truncate">{user.email}</div>
                        {user.banReason && (
                          <div className="text-xs text-destructive">
                            Ban: {user.banReason}
                          </div>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Select
                        value={user.role || 'free'}
                        onValueChange={(value) => onRoleChange(user.id, value as UserRole)}
                        disabled={user.id === currentUserId}
                      >
                        <SelectTrigger className="w-[140px] h-8">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="free">Free</SelectItem>
                          <SelectItem value="student">Student</SelectItem>
                          <SelectItem value="paid">Paid</SelectItem>
                          <SelectItem value="admin">Admin</SelectItem>
                        </SelectContent>
                      </Select>
                    </TableCell>
                    <TableCell>
                      {user.isBanned ? (
                        <Badge variant="destructive" className="gap-1">
                          <Ban className="h-3 w-3" /> Banned
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="gap-1">
                          <CheckCircle className="h-3 w-3" /> Active
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell>
                      {new Date(user.created_at).toLocaleDateString()}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-2">
                        <span className="font-medium">{availableTokens.toLocaleString()}</span>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-6 w-6 p-0"
                          onClick={() => setAdjustDialog({
                            userId: user.id,
                            email: user.email,
                            currentBalance: availableTokens,
                          })}
                        >
                          <Coins className="h-3 w-3" />
                        </Button>
                      </div>
                    </TableCell>
                    <TableCell className="text-right text-muted-foreground">
                      ${user.usage.totalCost.toFixed(4)}
                    </TableCell>
                    <TableCell className="text-right font-medium text-green-600">
                      ${(user.totalContributions || 0).toFixed(2)}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className={`flex items-center justify-end gap-1 font-semibold ${grossProfit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {grossProfit >= 0 ? <TrendingUp className="h-3 w-3" /> : <TrendingDown className="h-3 w-3" />}
                        ${Math.abs(grossProfit).toFixed(2)}
                      </div>
                    </TableCell>
                    <TableCell className="text-right">
                      {user.id !== currentUserId && (
                        <div className="flex justify-end gap-1">
                          {user.isBanned ? (
                            <Button variant="outline" size="sm" onClick={() => onUnban(user.id, user.email)}>
                              <CheckCircle className="h-4 w-4" />
                            </Button>
                          ) : (
                            <Button variant="destructive" size="sm" onClick={() => onBan(user.id, user.email)}>
                              <Ban className="h-4 w-4" />
                            </Button>
                          )}
                          <Button variant="destructive" size="sm" onClick={() => onDelete(user.id, user.email)}>
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      )}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      {/* Adjust Tokens Dialog */}
      <Dialog open={!!adjustDialog} onOpenChange={() => setAdjustDialog(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Adjust Tokens</DialogTitle>
            <DialogDescription>
              Adjust token balance for {adjustDialog?.email}
              <br />
              Current balance: <strong>{adjustDialog?.currentBalance.toLocaleString()}</strong> tokens
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Adjustment Type</Label>
              <Select value={adjustType} onValueChange={(v) => setAdjustType(v as typeof adjustType)}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="add">Add tokens</SelectItem>
                  <SelectItem value="subtract">Subtract tokens</SelectItem>
                  <SelectItem value="set">Set to exact amount</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Amount</Label>
              <Input
                type="number"
                min="0"
                value={adjustAmount}
                onChange={(e) => setAdjustAmount(e.target.value)}
                placeholder="Enter token amount"
              />
            </div>
            <div className="space-y-2">
              <Label>Reason</Label>
              <Select value={adjustReason} onValueChange={setAdjustReason}>
                <SelectTrigger>
                  <SelectValue placeholder="Select reason..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Customer support credit">Customer support credit</SelectItem>
                  <SelectItem value="Bug compensation">Bug compensation</SelectItem>
                  <SelectItem value="Promotional bonus">Promotional bonus</SelectItem>
                  <SelectItem value="Testing purposes">Testing purposes</SelectItem>
                  <SelectItem value="Manual correction">Manual correction</SelectItem>
                  <SelectItem value="Refund adjustment">Refund adjustment</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAdjustDialog(null)}>Cancel</Button>
            <Button onClick={handleAdjustTokens} disabled={isAdjusting}>
              {isAdjusting ? "Adjusting..." : "Confirm"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}